import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/app/components/ui/badge";
import { cn } from "@/app/handlers/cn.utils";
import { formatRelativeTime } from "@/app/handlers/utilityFunctions";

import { AssessmentRecord, FitAnswer } from "../config/api";
import { FitVerdict, ResolvedFit, resolveFit } from "../config/stats";

interface FitCheckCardProps {
	record: AssessmentRecord;
	index?: number;
	// Saved answers for this check; undefined while the answers query is still loading.
	answers?: FitAnswer[];
	onViewDetails: () => void;
}

// Same palette as the report's bar: on the CV, has-but-not-written, lacks, and gaps not yet sorted.
const SEG_CLASS = {
	fit: "bg-green-400",
	have: "bg-amber-400",
	miss: "bg-red-400",
	gray: "bg-gray-200",
} as const;

const VERDICT: Record<FitVerdict, { label: string; className: string }> = {
	strong: { label: "Strong fit", className: "bg-green-100 text-green-700" },
	partial: { label: "Partial fit", className: "bg-amber-100 text-amber-700" },
	weak: { label: "Weak fit", className: "bg-red-100 text-red-700" },
	review: { label: "Needs review", className: "bg-gray-100 text-gray-600" },
};

function fitSubline({ verdict, have, lack, unanswered }: ResolvedFit): string {
	if (verdict === "review") return `${unanswered} ${unanswered === 1 ? "gap" : "gaps"} to review`;

	const parts: string[] = [];
	if (lack > 0) parts.push(`${lack} real ${lack === 1 ? "gap" : "gaps"}`);
	if (have > 0) parts.push(`${have} to add to your CV`);
	return parts.length > 0 ? parts.join(" · ") : "Fully on your CV";
}

export const FitCheckCard = ({ record, index = 0, answers, onViewDetails }: FitCheckCardProps) => {
	const fit = resolveFit(record.assessment, answers);
	const { total, met, have, lack } = fit;
	const verdict = VERDICT[fit.verdict];

	// The bar reads like the report's: met must-haves first, then answered "have", then answered
	// "lack", then still-unanswered gaps in gray.
	const segments = Array.from({ length: total }, (_, i) =>
		i < met ? "fit" : i < met + have ? "have" : i < met + have + lack ? "miss" : "gray",
	);

	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onViewDetails}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onViewDetails();
				}
			}}
			style={{ animationDelay: `${index * 60}ms` }}
			className="group flex w-full cursor-pointer flex-col items-stretch rounded-lg border border-gray-200 bg-white p-5 text-left transition-all hover:border-gray-300 hover:shadow-sm motion-safe:animate-fc-rise"
		>
			<div className="mb-3 flex items-center justify-between gap-3">
				<Badge className={verdict.className}>{verdict.label}</Badge>
				<span className="flex items-center gap-1 text-xs text-gray-600">
					<Clock className="h-3 w-3" />
					{formatRelativeTime(record.createdAt)}
				</span>
			</div>

			<h3 className="font-inter text-base font-semibold text-gray-900">
				<Link
					to={`/jobs/${record.jobId}`}
					onClick={(e) => e.stopPropagation()}
					className="hover:text-primary-700 hover:underline"
				>
					{record.roleTitle || "Unknown position"}
				</Link>
			</h3>
			<p className="text-sm text-gray-600">{record.company || "Unknown company"}</p>

			{total > 0 ? (
				<div className="mt-4 flex gap-1">
					{segments.map((s, i) => (
						<span key={i} className={cn("h-2 flex-1 rounded-[3px]", SEG_CLASS[s])} />
					))}
				</div>
			) : (
				<div className="mt-4 h-2 rounded-[3px] bg-green-400" />
			)}

			<p className="mt-3 text-sm text-gray-600">{fitSubline(fit)}</p>

			<div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-3">
				<span className="inline-flex items-center gap-1 text-sm font-medium text-primary-700">
					View details
					<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
				</span>
			</div>
		</div>
	);
};

export default FitCheckCard;
